import axios from "axios";
import axiosInstance from "../../../middlewares/axiosInstance";
import { PaginationResponse, ScreenPermissionsResponse } from "../types";

export interface Order {
  order_id: string;
  session_id: string;
  store_id: string;
  branch_id: string;
  cashier_id: string;
  cashier_name: string;
  status: string;
  items: OrderItem[];
  payment: {
    method: string;
    status: string;
    transaction_id: string;
  };
  activity_logs: {
    action: string;
    changed_by: string;
    description: string;
    timestamp: number;
  }[];
  total_cost: number;
  raw_price: number;
  tax_amount: number;
  total_price: number;
  total_profit_margin: number;
  total_revenue: number;
  order_date: string;
  created_at: number;
  updated_at: number;
  is_deleted: boolean;
}

export interface OrderItem {
  item_id: string;
  name: string;
  price: number;
  quantity: number;
  total_item_cost: number;
}

export interface ListOrdersParams {
  pageIndex?: number;
  pageSize?: number;
  sortField?: string;
  sortOrder?: string;
  status?: string;
  from_date?: string;
  to_date?: string;
  search_text?: string;
  session_id?: string;
}

export interface OrdersListResponse extends PaginationResponse {
  orders: Order[];
}

export interface PosSession {
  _id: string;
  name: string;
  start_time: string;
  end_time?: string;
  status: string;
  total_sales?: number;
  business_id: string;
}

export interface ListSessionsParams {
  business_id: string;
  pageIndex?: number;
  pageSize?: number;
  sortField?: string;
  sortOrder?: string;
}

export interface SessionsListResponse extends PaginationResponse {
  sessions: PosSession[];
}

export interface GetBusinessIdResponse {
  business_id: string;
}

class AdminOrdersService {
  private readonly baseUrl = "/pos/orders";

  async getBusinessIdForUser(userId: string): Promise<string | null> {
    try {
      const response = await axiosInstance.post("/pos/users/business-id", {
        user_id: userId,
      });

      console.log("Business ID response:", response.data);

      if (response.data && response.data.success && response.data.data) {
        // Check for store_id in the response as per POS users index structure
        return (
          response.data.data.store_id || response.data.data.business_id || null
        );
      }

      return null;
    } catch (error) {
      console.error("Error fetching business ID for user:", error);
      return null;
    }
  }

  async listSessions(
    params: ListSessionsParams
  ): Promise<SessionsListResponse> {
    try {
      const response = await axiosInstance.post(
        "/pos/daybook/sessions/business",
        {
          business_id: params.business_id,
          pageIndex: params.pageIndex || 0,
          pageSize: params.pageSize || 10,
          sortField: params.sortField || "start_time",
          sortOrder: params.sortOrder || "desc",
        }
      );

      console.log("Sessions response:", response.data);

      let sessionsData: PosSession[] = [];
      let totalItems = 0;

      if (response.data && response.data.success && response.data.data) {
        if (Array.isArray(response.data.data.items)) {
          // Map the new response structure to our PosSession interface
          sessionsData = response.data.data.items.map((item: any) => ({
            _id: item.id,
            name: `Session ${item.id.substring(0, 8)}`,
            start_time: new Date(item.timestamps.start).toISOString(),
            end_time: item.timestamps.end
              ? new Date(item.timestamps.end).toISOString()
              : undefined,
            status: item.status,
            total_sales: item.amounts.total_sales,
            business_id: item.business.id,
          }));
          totalItems = response.data.data.total || 0;
        } else if (Array.isArray(response.data.data)) {
          sessionsData = response.data.data;
          totalItems = response.data.metadata?.total?.value || 0;
        } else if (
          response.data.data &&
          Array.isArray(response.data.data.sessions)
        ) {
          sessionsData = response.data.data.sessions;
          totalItems = response.data.data.total || 0;
        } else if (Array.isArray(response.data.sessions)) {
          sessionsData = response.data.sessions;
          totalItems = response.data.total || 0;
        }
      }

      return {
        sessions: sessionsData,
        total: totalItems,
        pageIndex: params.pageIndex || 0,
        pageSize: params.pageSize || 10,
      };
    } catch (error) {
      console.error("Error fetching POS sessions:", error);
      return {
        sessions: [],
        total: 0,
        pageIndex: 0,
        pageSize: 10,
      };
    }
  }

  async listOrdersForSession(
    sessionId: string,
    params: Omit<ListOrdersParams, "session_id">
  ): Promise<OrdersListResponse> {
    return this.listOrders({
      ...params,
      session_id: sessionId,
    });
  }

  async listOrders(params: ListOrdersParams): Promise<OrdersListResponse> {
    try {
      const requestParams = {
        session_id: params.session_id,
        pageIndex: params.pageIndex || 0,
        pageSize: params.pageSize || 10,
        sortField: params.sortField || "created_at",
        sortOrder: params.sortOrder || "desc",
        status: params.status || undefined,
        from_date: params.from_date || undefined,
        to_date: params.to_date || undefined,
        search_text: params.search_text || undefined,
      };

      console.log("Fetching orders with params:", requestParams);
      const response = await axiosInstance.post(
        `${this.baseUrl}/list`,
        requestParams
      );
      console.log("Orders response:", response.data);

      let ordersData: Order[] = [];
      let totalItems = 0;

      if (response.data && response.data.success) {
        if (response.data.data && Array.isArray(response.data.data.items)) {
          // Map the new response structure to our Order interface
          ordersData = response.data.data.items.map((item: any) => ({
            order_id: item.order_id,
            session_id: item.session_id,
            store_id: item.store_id,
            branch_id: item.branch_id,
            cashier_id: item.cashier_id,
            cashier_name: item.cashier_name,
            status: item.status,
            items: (item.items || []).map((orderItem: any) => ({
              item_id: orderItem.item_id,
              name: orderItem.name,
              price: orderItem.price,
              quantity: orderItem.quantity,
              total_item_cost: orderItem.price * orderItem.quantity,
            })),
            payment: {
              method: item.payment?.method || "unknown",
              status: item.payment?.status || "unknown",
              transaction_id: item.payment?.transaction_id || "unknown",
            },
            activity_logs: (item.activity_logs || []).map((log: any) => ({
              action: log.action,
              changed_by: log.changed_by,
              description: log.description,
              timestamp: log.timestamp,
            })),
            total_cost: item.total_cost || 0,
            raw_price: item.raw_price || 0,
            tax_amount: item.tax_amount || 0,
            total_price: item.total_price || 0,
            total_profit_margin: item.total_profit_margin || 0,
            total_revenue: item.total_revenue || 0,
            order_date:
              item.order_date || new Date(item.created_at).toISOString(),
            created_at: item.created_at,
            updated_at: item.updated_at,
            is_deleted: item.is_deleted || false,
          }));
          totalItems = response.data.data.total || 0;
        } else if (Array.isArray(response.data.data)) {
          ordersData = response.data.data;
          totalItems = response.data.metadata?.total?.value || 0;
        } else if (
          response.data.data &&
          Array.isArray(response.data.data.orders)
        ) {
          ordersData = response.data.data.orders;
          totalItems = response.data.data.total || 0;
        } else if (Array.isArray(response.data.orders)) {
          ordersData = response.data.orders;
          totalItems = response.data.total || 0;
        }
      }

      return {
        orders: ordersData,
        total: totalItems,
        pageIndex: params.pageIndex || 0,
        pageSize: params.pageSize || 10,
      };
    } catch (error) {
      console.error("Error fetching orders:", error);
      return {
        orders: [],
        total: 0,
        pageIndex: 0,
        pageSize: 10,
      };
    }
  }

  async listOrdersForAllSessions(
    businessId: string,
    params: Omit<ListOrdersParams, "session_id">
  ): Promise<OrdersListResponse> {
    try {
      // First, get all sessions for the business
      const sessionsResponse = await this.listSessions({
        business_id: businessId,
        pageSize: 100, // Get a large number of sessions to ensure we get all
        sortOrder: "desc", // Latest sessions first
      });

      const sessions = sessionsResponse.sessions;

      if (!sessions || sessions.length === 0) {
        console.log("No sessions found for business:", businessId);
        return {
          orders: [],
          total: 0,
          pageIndex: params.pageIndex || 0,
          pageSize: params.pageSize || 10,
        };
      }

      console.log(`Found ${sessions.length} sessions for business`);

      // Collect all orders from all sessions
      let allOrders: Order[] = [];
      let totalOrders = 0;

      // For simplicity, limit to the first 5 active sessions to avoid too many requests
      const activeSessions = sessions.slice(0, 5);

      for (const session of activeSessions) {
        console.log(`Loading orders for session: ${session._id}`);
        const ordersResponse = await this.listOrdersForSession(
          session._id,
          params
        );
        if (ordersResponse.orders && ordersResponse.orders.length > 0) {
          console.log(
            `Found ${ordersResponse.orders.length} orders for session ${session._id}`
          );
          allOrders = [...allOrders, ...ordersResponse.orders];
          totalOrders += ordersResponse.total;
        }
      }

      // Sort all orders by date (newest first)
      allOrders.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      // Apply pagination manually since we've combined results from multiple sessions
      const pageIndex = params.pageIndex || 0;
      const pageSize = params.pageSize || 10;
      const startIndex = pageIndex * pageSize;
      const paginatedOrders = allOrders.slice(
        startIndex,
        startIndex + pageSize
      );

      return {
        orders: paginatedOrders,
        total: allOrders.length,
        pageIndex: pageIndex,
        pageSize: pageSize,
      };
    } catch (error) {
      console.error("Error fetching orders for all sessions:", error);
      return {
        orders: [],
        total: 0,
        pageIndex: params.pageIndex || 0,
        pageSize: params.pageSize || 10,
      };
    }
  }

  async getOrderDetails(orderId: string): Promise<Order | null> {
    try {
      const response = await axiosInstance.get(`${this.baseUrl}/${orderId}`);
      console.log("Order details response:", response.data);

      if (response.data && response.data.success && response.data.data) {
        const orderData = response.data.data;
        return {
          order_id: orderData.order_id,
          session_id: orderData.session_id,
          store_id: orderData.store_id,
          branch_id: orderData.branch_id,
          cashier_id: orderData.cashier_id,
          cashier_name: orderData.cashier_name,
          status: orderData.status,
          items: (orderData.items || []).map((orderItem: any) => ({
            item_id: orderItem.item_id,
            name: orderItem.name,
            price: orderItem.price,
            quantity: orderItem.quantity,
            total_item_cost: orderItem.price * orderItem.quantity,
          })),
          payment: {
            method: orderData.payment?.method || "unknown",
            status: orderData.payment?.status || "unknown",
            transaction_id: orderData.payment?.transaction_id || "unknown",
          },
          activity_logs: (orderData.activity_logs || []).map((log: any) => ({
            action: log.action,
            changed_by: log.changed_by,
            description: log.description,
            timestamp: log.timestamp,
          })),
          total_cost: orderData.total_cost || 0,
          raw_price: orderData.raw_price || 0,
          tax_amount: orderData.tax_amount || 0,
          total_price: orderData.total_price || 0,
          total_profit_margin: orderData.total_profit_margin || 0,
          total_revenue: orderData.total_revenue || 0,
          order_date:
            orderData.order_date ||
            new Date(orderData.created_at).toISOString(),
          created_at: orderData.created_at,
          updated_at: orderData.updated_at,
          is_deleted: orderData.is_deleted || false,
        };
      }

      return null;
    } catch (error) {
      console.error("Error fetching order details:", error);
      return null;
    }
  }

  async updateOrderStatus(orderId: string, status: string): Promise<boolean> {
    try {
      const response = await axiosInstance.put(
        `${this.baseUrl}/status/${orderId}`,
        { status }
      );
      console.log("Update status response:", response.data);
      return response.data && response.data.success;
    } catch (error) {
      console.error("Error updating order status:", error);
      return false;
    }
  }

  async getOrderPermissions(): Promise<ScreenPermissionsResponse> {
    try {
      const response = await axiosInstance.get("/permissions/screen/orders");
      return response.data;
    } catch (error) {
      console.error("Error fetching order permissions:", error);
      return { permissions: {} };
    }
  }
  async listBusinessOrders(
    params: Omit<ListOrdersParams, "session_id">
  ): Promise<any> {
    try {
      const response = await axiosInstance.post(
        "/business-admin/orders-list/list",
        {
          pageIndex: params.pageIndex || 0,
          pageSize: params.pageSize || 10,
          sortField: params.sortField || "created_at",
          sortOrder: params.sortOrder || "desc",
          searchQuery: params.search_text || undefined,
          startDate: params.from_date || undefined,
          endDate: params.to_date || undefined,
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching listBusinessOrders:", error);
      throw error;
    }
  }
}

export const adminOrdersService = new AdminOrdersService();
